import { Component, OnDestroy, OnInit } from '@angular/core';
import { HelpService } from '../../shared/service/help.service';
import { HELP_MAKE_FIELD_MAP } from '../../app.constants';
import { JhiLanguageService } from '../../shared/core-utils';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '../../shared/alert/alert.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ParamContext } from '../../shared/service/param.context';
import { StudyEditorService } from '../../study-editor/study-editor.service';
import { EnvironmentFieldmapInfoItem } from './select-environment-field-map-modal.component';
import { FieldMapContext } from './field-map.context';
import { FieldMapPlotModel } from './field-map-plot.model';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { FieldmapService } from '../../field-management/service/fieldmap.service';
import { FieldMapSettingsModel } from './field-map-settings.model';
import { finalize } from 'rxjs/internal/operators/finalize';
import { FieldMapModel } from './field-map.model';
import { HttpErrorResponse } from '@angular/common/http';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FileDownloadHelper } from '../../entities/sample/file-download.helper';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-field-plan',
    templateUrl: './field-map.component.html',
    styleUrls: ['./field-map.component.scss']
})
export class FieldMapComponent implements OnInit, OnDestroy {
    isViewFieldMap: boolean = false;
    fieldPlanStep: number = 1;
    helpLink: string;
    isLoading = false;
    studyName: string;
    studyId: number;
    selectedEnvironments: EnvironmentFieldmapInfoItem[];
    totalNumberOfPlotsNeeded: number = 0;
    locationId: number;
    rowsInBlock: number = 0;
    rangesInBlock: number = 0;
    rowsPerPlot: number = 1;
    column: number = 1;
    range: number = 1;
    totalNumberOfPlots: number;
    isSerpentine: boolean;
    rowCapacity: number = 1;
    rowCapacityOptions: number[] = [1, 2, 4, 8];
    fieldMapPlots: FieldMapPlotModel[][] = [];
    deletedPlotCoordinates: string[] = [];
    blockName: string;

    subscriptionManager = new SubscriptionsManager();

    constructor(private route: ActivatedRoute,
                private languageService: JhiLanguageService,
                private alertService: AlertService,
                public activeModal: NgbActiveModal,
                private paramContext: ParamContext,
                public translateService: TranslateService,
                private helpService: HelpService,
                private router: Router,
                private studyEditorService: StudyEditorService,
                private fieldMapContext: FieldMapContext,
                public fieldMapService: FieldmapService,
                private fileDownloadHelper: FileDownloadHelper) {
    }

    ngOnInit(): void {
        this.studyName = this.fieldMapContext.studyName;
        const params = this.route.snapshot.queryParams;
        this.studyId = params['studyId'];
        // Get helplink url
        if (!this.helpLink || !this.helpLink.length) {
            this.helpService.getHelpLink(HELP_MAKE_FIELD_MAP).toPromise().then((response) => {
                this.helpLink = response.body;
            }).catch((error) => {});
        }
        if (this.fieldMapContext.selectedEnvironments) {
            this.fieldPlanStep = 1;
            this.selectedEnvironments = this.fieldMapContext.selectedEnvironments;
            this.selectedEnvironments.forEach((env) => {
                this.totalNumberOfPlotsNeeded += env.plotsCount;
            });
            this.locationId = this.selectedEnvironments[0].locationId;
        } else if (this.fieldMapContext.instanceId) {
            this.isLoading = true;
            this.isViewFieldMap = true;
            this.fieldPlanStep = 0;
            this.subscriptionManager.add = this.fieldMapService.viewFieldMap(this.studyId, this.fieldMapContext.instanceId).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe((resp: FieldMapModel) => {
                this.blockName = resp.blockName;
                this.fieldMapPlots = resp.plots;
                this.rowsInBlock = resp.settings.rowsInBlock;
                this.rangesInBlock = resp.settings.rangesInBlock;
                this.rowsPerPlot = resp.settings.rowsPerPlot;
                this.isSerpentine = resp.settings.isSerpentine;
                this.rowCapacity = resp.settings.machineCapacity;
                this.deletedPlotCoordinates = resp.settings.deletedPlotCoordinates;
                this.column = resp.settings.column;
                this.range = resp.settings.range;
                this.fieldMapService.getEnvironmentFieldmapInfo(this.studyId, resp.settings.selectedEnvironments).pipe(
                    finalize(() => this.isLoading = false)
                ).subscribe((selectedEnvironmentsInfo) => {
                    this.selectedEnvironments = selectedEnvironmentsInfo as EnvironmentFieldmapInfoItem[];
                    this.selectedEnvironments.forEach((env) => {
                        this.totalNumberOfPlotsNeeded += env.plotsCount;
                    });
                });
            }, () => this.isLoading = false);
        }
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    back($event) {
        if (this.fieldPlanStep === 1 || this.isViewFieldMap) {
            this.studyEditorService.navigateToTab(this.studyId, 'study-settings');
        } else {
            if (this.fieldPlanStep === 3) {
                this.fieldMapPlots.forEach((row) => {
                    row.forEach((item) => {
                        item.plotNo = null;
                        item.entryNo = null;
                        item.repNo = null;
                    });
                });
            }
            this.fieldPlanStep--;
        }
    }

    continue($event) {
        if (this.fieldPlanStep === 1 && this.isFieldDetailsValid) {
            this.generateDefaultFieldMapPlots();
            this.fieldPlanStep = 2;
        } else if (this.fieldPlanStep === 2 && this.isPlantingDetailsValid()) {
            this.isLoading = true;
            const settings = this.createFieldMapSettings();
            this.subscriptionManager.add = this.fieldMapService.generateFieldmapPlots(this.studyId, settings).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe((resp: FieldMapModel) => {
                this.fieldMapPlots = resp.plots;
                this.fieldPlanStep = 3;
            }, () => this.isLoading = false);
        }
    }

    saveFieldMap($event) {
        this.isLoading = true;
        const settings = this.createFieldMapSettings();
        this.subscriptionManager.add = this.fieldMapService.saveFieldMap(this.studyId, settings).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe(() => {
            this.studyEditorService.navigateToTab(this.studyId, 'study-settings');
        }, () => this.isLoading = false);
    }

    createFieldMapSettings(): FieldMapSettingsModel {
        const settings = new FieldMapSettingsModel();
        settings.column = this.column;
        settings.range = this.range;
        settings.rowsInBlock = this.rowsInBlock;
        settings.rangesInBlock = this.rangesInBlock;
        settings.rowsPerPlot = this.rowsPerPlot;
        settings.selectedEnvironments = this.selectedEnvironments.map(env => env.instanceId);
        settings.isSerpentine = this.isSerpentine;
        settings.machineCapacity =  this.rowCapacity;
        settings.deletedPlotCoordinates = this.deletedPlotCoordinates;
        return settings;
    }

    private isPlantingDetailsValid(): boolean {
        if (this.column > (this.rowsInBlock / this.rowsPerPlot)) {
            this.alertService.error('study-editor.field-map.column.error', { param: this.rowsInBlock / this.rowsPerPlot });
            return false;
        }
        if (this.range > (this.rangesInBlock)) {
            this.alertService.error('study-editor.field-map.column.error', { range: this.rangesInBlock });
            return false;
        }
        if (this.isSerpentine === null || this.isSerpentine === undefined) {
            this.alertService.error('study-editor.field-map.plot.layout.order.required');
            return false;
        }
        if (this.totalNumberOfPlotsNeeded > this.getRemainingPlots()) {
            this.alertService.error('study-editor.field-map.planting.details.error');
            return false;
        }
        return true;
    }

    private isFieldDetailsValid(): boolean {
        if (this.rowsInBlock % this.rowsPerPlot !== 0) {
            this.alertService.error('study-editor.field-map.rows.in.block.error');
            return false;
        }
        if (this.totalNumberOfPlotsNeeded > this.totalNumberOfPlots) {
            this.alertService.error('study-editor.field-map.total.number.of.plots.error');
            return false;
        }
        return true;
    }

    getSequence(start: number, end: number): number[] {
        const result: number[] = [];
        if (start <= end) {
            for (let i = start; i <= end; i++) {
                result.push(i);
            }
        } else {
            for (let i = start; i >= end; i--) {
                result.push(i);
            }
        }
        return result;
    }

    getRemainingPlots(): number {
        const startingColumn = this.column - 1;
        const startingRange = this.range - 1;
        const totalRange = this.rowsInBlock / this.rowsPerPlot;

        let remaining = 0;
        let isStartOk = false;
        let leftToRight = true;

        for (let y = 0; y < this.rangesInBlock; y++) {
            const xStart = leftToRight ? 0 : totalRange - 1;
            const xEnd = leftToRight ? totalRange : -1;
            const xStep = leftToRight ? 1 : -1;

            for (let x = xStart; x !== xEnd; x += xStep) {
                if (x === startingColumn && y === startingRange) {
                    isStartOk = true;
                }

                if (isStartOk && !this.fieldMapPlots[x][y].isPlotDeleted) {
                    remaining++;
                }
            }

            if (this.isSerpentine) {
                leftToRight = !leftToRight;
            }
        }

        return remaining;
    }

    // Check if fieldmap is empty
    isFieldmapEmpty(): boolean {
        return !this.fieldMapPlots || this.fieldMapPlots.length === 0;
    }

    // Get plot data safely
    getPlot(col: number, row: number): FieldMapPlotModel | null {
        if (this.isFieldmapEmpty() ||
            col >= this.fieldMapPlots.length ||
            row >= this.fieldMapPlots[col]?.length) {
            return null;
        }
        return this.fieldMapPlots[col][row];
    }

    // Get remainder direction for machine arrows
    getRemainderDirection(): 'up' | 'down' | null {
        const j = Math.floor(this.rowsInBlock / this.rowCapacity) + 1;
        if (j % 2 === 1) {
            return 'up';
        } else if (j % 2 === 0) {
            return 'down';
        }
        return null;
    }

    // Event handlers
    onPlotClick(col: number, row: number): void {
        if (this.fieldPlanStep === 2) {
            const plot = this.getPlot(col, row);
            if (!plot) {
                return;
            }

            plot.isPlotDeleted = !plot.isPlotDeleted;

            // Update marked cells and validate
            this.updateMarkedCells();
        }
    }

    onRangeHeaderClick(rangeIndex: number): void {
        if (this.fieldPlanStep === 2) {
            const rangeElements = this.getRangeElements(rangeIndex);
            const allUnusable = this.checkAllUnusable(rangeElements);
            this.setArrayToUnusable(rangeElements, allUnusable);
            this.updateMarkedCells();
        }
    }

    onColumnHeaderClick(columnIndex: number): void {
        if (this.fieldPlanStep === 2) {
            const columnElements = this.getColumnElements(columnIndex);
            const allUnusable = this.checkAllUnusable(columnElements);
            this.setArrayToUnusable(columnElements, allUnusable);
            this.updateMarkedCells();
        }
    }

    drop(event: CdkDragDrop<any>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(this.selectedEnvironments,
                event.previousIndex,
                event.currentIndex);
        }
    }

    // Get all plots in a specific range (row)
    private getRangeElements(rangeIndex: number): FieldMapPlotModel[] {
        const elements: FieldMapPlotModel[] = [];
        const cols = Math.floor(this.rowsInBlock / this.rowsPerPlot);

        for (let col = 0; col < cols; col++) {
            const plot = this.getPlot(col, rangeIndex);
            if (plot) {
                elements.push(plot);
            }
        }
        return elements;
    }

    // Get all plots in a specific column
    private getColumnElements(columnIndex: number): FieldMapPlotModel[] {
        const elements: FieldMapPlotModel[] = [];

        for (let row = 0; row < this.rangesInBlock; row++) {
            const plot = this.getPlot(columnIndex, row);
            if (plot) {
                elements.push(plot);
            }
        }
        return elements;
    }

    // Check if all plots in array are unusable (deleted or planted)
    private checkAllUnusable(plots: FieldMapPlotModel[]): boolean {
        return plots.every(plot => plot.isPlotDeleted);
    }

    // Set array of plots to usable or unusable
    private setArrayToUnusable(plots: FieldMapPlotModel[], allUnusable: boolean): void {
        plots.forEach(plot => {
            if (allUnusable) {
                plot.isPlotDeleted = false;
            } else {
                plot.isPlotDeleted = true;
            }
        });
    }

    // Update marked cells string
    private updateMarkedCells(): void {
        this.deletedPlotCoordinates = [];

        if (!this.isFieldmapEmpty()) {
            for (let col = 0; col < this.fieldMapPlots.length; col++) {
                for (let row = 0; row < this.fieldMapPlots[col].length; row++) {
                    const plot = this.fieldMapPlots[col][row];
                    if (plot && plot.isPlotDeleted) {
                        this.deletedPlotCoordinates.push(`${col}_${row}`);
                    }
                }
            }
        }
    }

    private generateDefaultFieldMapPlots() {
        const cols = Math.floor(this.rowsInBlock / this.rowsPerPlot);
        const rows = this.rangesInBlock;

        for (let col = 0; col < cols; col++) {
            this.fieldMapPlots[col] = [];
            for (let row = 0; row < rows; row++) {
                this.fieldMapPlots[col][row] = {
                    isPlotDeleted: false
                };
            }
        }
    }

    disableNextButton() {
        return !(this.rowsInBlock > 0 && this.rowsPerPlot > 0 && this.rangesInBlock > 0);
    }

    onPlotDetailsChange() {
        this.totalNumberOfPlots = null;
        if (!(this.rowsInBlock > 0 && this.rowsPerPlot > 0 && this.rangesInBlock > 0)) {
            return;
        }
        this.totalNumberOfPlots = this.rowsInBlock / this.rowsPerPlot * this.rangesInBlock;
        this.totalNumberOfPlots = this.totalNumberOfPlots > 0 && this.totalNumberOfPlots % 1 === 0 ? this.totalNumberOfPlots : null;
    }

    getPlotLayoutOrder(): string {
        return this.isSerpentine ? this.translateService.instant('study-editor.field-map.serpentine') :
            this.translateService.instant('study-editor.field-map.row.column');
    }

    getBlockCapacity(): string {
        return this.rowsInBlock + ' ' + this.translateService.instant('study-editor.field-map.rows') + ', ' + this.rangesInBlock + ' ' + this.translateService.instant('study-editor.field-map.ranges');
    }

    getStartingCoordinates(): string {
        return this.translateService.instant('study-editor.field-map.column.prefix') + ' ' + this.column + ', '
            + this.translateService.instant('study-editor.field-map.range.prefix') + ' ' + this.range;
    }

    exportToExcel() {
        const settings = this.createFieldMapSettings();
        this.subscriptionManager.add = this.fieldMapService.exportFieldmap(this.studyId, this.isViewFieldMap, settings).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe((response: any) => {
            const fileName = this.fileDownloadHelper.getFileNameFromResponseContentDisposition(response);
            this.fileDownloadHelper.save(response.body, fileName);
        });
    }

    getDisplayString(plot: FieldMapPlotModel): string {
        return plot.plotNo ? this.studyName + '-' + plot.plotNo + '<br/>Entry ' + plot.entryNo + '<br/>Rep ' + plot.repNo : '';
    }

    protected readonly Math = Math;
}
